import { apiRequest } from '@scc/lib'
import type {
  LaunchProjectionRepository, CreateLaunchProjectionInput,
} from '@/data/repositories/types'
import type {
  LaunchProjection, ProjectionOutputs, TemplateConfig,
  LaunchProjectionRevisionSummary, LaunchProjectionRevision,
  WizardConfig,
} from '@/modules/launch/templating-types'

const base = (agencyId: number, storeId: number) =>
  `/agencies/${agencyId}/stores/${storeId}/launch-projections`

export function createApiLaunchProjectionRepository(): LaunchProjectionRepository {
  return {
    // Agency-scoped
    compute: (agencyId, storeId, config: TemplateConfig) =>
      apiRequest<ProjectionOutputs>(`${base(agencyId, storeId)}/compute`, { method: 'POST', body: { config } }),
    create: (agencyId, storeId, input: CreateLaunchProjectionInput) =>
      apiRequest<LaunchProjection>(base(agencyId, storeId), { method: 'POST', body: input }),
    list: (agencyId, storeId) =>
      apiRequest<LaunchProjection[]>(base(agencyId, storeId)),
    get: (agencyId, storeId, id) =>
      apiRequest<LaunchProjection>(`${base(agencyId, storeId)}/${id}`),
    remove: async (agencyId, storeId, id) => {
      await apiRequest<void>(`${base(agencyId, storeId)}/${id}`, { method: 'DELETE' })
    },
    getActive: (agencyId, storeId) =>
      apiRequest<LaunchProjection | null>(`${base(agencyId, storeId)}/active`),
    lock: (agencyId, storeId, id, startMonth) =>
      apiRequest<LaunchProjection>(`${base(agencyId, storeId)}/${id}/lock`, { method: 'POST', body: startMonth ? { startMonth } : {} }),
    unlock: (agencyId, storeId, id) =>
      apiRequest<LaunchProjection>(`${base(agencyId, storeId)}/${id}/unlock`, { method: 'POST' }),
    setStartMonth: (agencyId, storeId, id, startMonth) =>
      apiRequest<LaunchProjection>(`${base(agencyId, storeId)}/${id}/start-month`, { method: 'POST', body: { startMonth } }),
    // Revisions — agency-scoped
    listRevisions: (agencyId, storeId, id) =>
      apiRequest<LaunchProjectionRevisionSummary[]>(`${base(agencyId, storeId)}/${id}/revisions`),
    getRevision: (agencyId, storeId, id, rev) =>
      apiRequest<LaunchProjectionRevision>(`${base(agencyId, storeId)}/${id}/revisions/${rev}`),
    // Amend — agency-scoped
    amend: (agencyId, storeId, id, config, note, lockedMonths) =>
      apiRequest<LaunchProjection>(`${base(agencyId, storeId)}/${id}/amend`, {
        method: 'POST',
        body: {
          config,
          ...(note !== undefined ? { note } : {}),
          ...(lockedMonths !== undefined ? { lockedMonths } : {}),
        },
      }),
    // Launch Wizard — agency-scoped
    createFromWizard: (agencyId, storeId, name: string, wizard: WizardConfig, kind) =>
      apiRequest<LaunchProjection>(`${base(agencyId, storeId)}/from-wizard`, { method: 'POST', body: { name, wizard, kind } }),
    updateFromWizard: (agencyId, storeId, id: string, name: string, wizard: WizardConfig) =>
      apiRequest<LaunchProjection>(`${base(agencyId, storeId)}/${id}/from-wizard`, { method: 'PUT', body: { name, wizard } }),
    // Overwrite a draft's full config (3-channel builder edit path)
    updateConfig: (agencyId, storeId, id: string, name: string, config: TemplateConfig, kind) =>
      apiRequest<LaunchProjection>(`${base(agencyId, storeId)}/${id}/config`, { method: 'PUT', body: kind ? { name, config, kind } : { name, config } }),
    // Snapshot sharing — mint / revoke the public share token
    enableShare: (agencyId, storeId, id: string, expiresAt?: Date | null) =>
      apiRequest<{ shareToken: string }>(`${base(agencyId, storeId)}/${id}/share`, {
        method: 'POST',
        body: expiresAt === undefined
          ? {}
          : { expiresAt: expiresAt === null ? null : expiresAt.toISOString() },
      }),
    disableShare: async (agencyId, storeId, id: string) => {
      await apiRequest<void>(`${base(agencyId, storeId)}/${id}/share`, { method: 'DELETE' })
    },
  }
}
